import api from '../api';

export const clientService = {
  list: (params) => api.get("/clients", { params }),
  /** DCC projects to map against (same rationale as the practice mapping). */
  dccCandidates: (search) =>
    api.get("/clients/dcc-candidates", { params: search ? { search } : {} }),
  /** Active SpoonFeed locations for add-location dropdown + autofill. */
  locationTemplates: () => api.get("/clients/location-templates"),
  linkDcc: (id, commandCenterId) => api.post(`/clients/${id}/link/dcc`, { commandCenterId }),
  unlinkDcc: (id) => api.delete(`/clients/${id}/link/dcc`),
  /** Hard-delete locally; DentalIQ office removed (recoverable) + DCC project
   *  deactivated. Distinct from archive, which only flips status. */
  purge: (id) => api.delete(`/clients/${id}/purge`),
  get: (id) => api.get(`/clients/${id}`),
  /** Where this location is across the 11-stage lifecycle + the next action. */
  journey: (id) => api.get(`/clients/${id}/journey`),
  /** Practice Location Edit → Client email notifications (saved with the location's PATCH). */
  emailNotifications: (id) => api.get(`/clients/${id}/email-notifications`),
  /** Mark/unmark a location as onboarded (drives the Journey's onboard stage). */
  setOnboarded: (id, onboarded = true) => api.post(`/clients/${id}/onboarded`, { onboarded }),
  create: (data) => api.post("/clients", data),
  /** Copy a location into a new paused clients row under the same practice. */
  duplicate: (id) => api.post(`/clients/${id}/duplicate`),
  update: (id, data) => api.patch(`/clients/${id}`, data),
  /** Client Mapping tab: clients with active services + Google/Meta campaign managers. */
  mappings: (params) => api.get("/clients/mappings", { params }),
  /** Save a client's campaign-manager assignments from the Client Mapping tab. */
  updateMapping: (id, data) => api.patch(`/clients/${id}/mapping`, data),
  archive: (id) => api.delete(`/clients/${id}`),
  restore: (id) => api.post(`/clients/${id}/restore`),
  /** Push location → DCC and parent practice → DentalIQ. */
  push: (id) => api.post(`/clients/${id}/push`),
  /** Location -> DCC. DCC is location-level, so this is the Location screen's push. */
  pushDcc: (id) => api.post(`/clients/${id}/push/dcc`),
  serpstatProjects: () => api.get("/clients/serpstat-projects"),
  commandCenterLinkPreview: () => api.get("/clients/command-center/preview"),
  commandCenterLink: (links) =>
    api.post("/clients/command-center/link", { links }),
  fusionPreview: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/clients/fusion/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  fusionApply: (rows) => api.post("/clients/fusion/apply", { rows }),
  /** Bulk import per-service Active/Inactive from the DPM clients sheet. */
  activeServicesPreview: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/clients/active-services/preview", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
  },
  activeServicesApply: (rows) =>
    api.post("/clients/active-services/apply", { rows }, { timeout: 180000 }),
  /** Build a sample .xlsx (TRUE/FALSE dropdowns) from the given rows — returns a Blob. */
  activeServicesSample: (rows) =>
    api.post("/clients/active-services/sample", { rows }, { responseType: "blob" }),
  /** Active locations only. Services and campaign managers are separate sheets. */
  activeServicesActiveSample: (rows) =>
    api.post("/clients/active-services/sample-active", { rows }, { responseType: "blob" }),
  /**
   * Competitor ads research brought in as a file — a rival's ad deck, an
   * exported auction-insights PDF, a manual teardown. Stored and attributed;
   * deliberately not parsed.
   */
  uploadCompetitorAdsDoc: (clientId, file, note = "") => {
    const fd = new FormData();
    fd.append("file", file);
    if (note) fd.append("note", note);
    return api.post(`/clients/${clientId}/competitor-ads`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
  },
  deleteCompetitorAdsDoc: (clientId, docId) =>
    api.delete(`/clients/${clientId}/competitor-ads/${docId}`),
  /** Scrape landing page via Firecrawl and extract location fields. */
  extractLanding: (url) => api.post("/clients/extract-landing", { url }),
  /** Scrape the location website via Firecrawl and fill Brand & web fields. */
  extractBrand: (url) => api.post("/clients/extract-brand", { url }, { timeout: 180000 }),
  /** Practice coordinates from Google Places. Returns them; saving stays the form's job. */
  geocode: (id, address) => api.post(`/clients/${id}/geocode`, address ? { address } : {}),
  /** Nearby competitor domains from Google Places. Returns them; saving is the editor's job. */
  findCompetitors: (id, radiusMiles) => api.post(
    `/clients/${id}/find-competitors`, radiusMiles ? { radiusMiles } : {},
  ),
  dedupPreview: () => api.get("/clients/dedup/preview"),
  dedupApply: (merges) => api.post("/clients/dedup/apply", { merges }),
  auditStatus: (id) => api.get(`/clients/${id}/audit-status`),
  pipelineStatus: (id) => api.get(`/clients/${id}/pipeline-status`),
  toolReadiness: (id) => api.get(`/clients/${id}/tool-readiness`),
  /**
   * Select-time gate: is this tool's mapped service ON for the location?
   * Returns `{ active, reason, clientName, serviceKeys }`.
   */
  toolAccess: (id, toolKey) =>
    api.get(`/clients/${id}/tool-access`, { params: { tool: toolKey } }),
  /** Operational checklist — what must be true before any tool is worth running. */
  checklist: (id) => api.get(`/clients/${id}/checklist`),
  /** Every store holding config for one client, grouped by what it unlocks. */
  setup: (id) => api.get(`/clients/${id}/setup`),
  /**
   * Which services Command Center has a department row for. Asked before a
   * toggle is allowed to move, because a service it has no row for cannot be
   * written there — see `lib/dcc-service-map#syncableServices`. Fails open, so
   * an unreachable Command Center blocks nothing.
   */
  syncableServices: (id) => api.get(`/clients/${id}/syncable-services`),
  /**
   * The setup fields that live in a satellite collection (Ads Audit settings,
   * Market Intel settings, Leads config). Separate from `update` because these
   * go through each owning collection's own writer, side effects and all —
   * `fields` is keyed on the setup field key, with one array of cells per field.
   */
  updateSetup: (id, fields) => api.patch(`/clients/${id}/setup`, { fields }),
  /** Data-quality problems across the book. Needs client-management rights. */
  dataHygiene: () => api.get('/clients/hygiene'),
  /** Agency-wide readiness — verdicts for every client in one call. */
  readinessMatrix: (params) => api.get(`/clients/readiness/matrix`, { params }),
  /** Registry-backed per-tool readiness. `tools` optionally narrows the set. */
  toolChecklist: (id, tools) =>
    api.get(`/clients/${id}/tool-checklist`, {
      params: tools?.length ? { tools: tools.join(',') } : undefined,
    }),
  crawlRegistry: (id) => api.post(`/clients/${id}/registry/crawl`),
};
